import React from 'react';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import './Legal.css';

const Privacy: React.FC = () => {
  return (
    <div className="legal-page">
      <SEO 
        title="Privacy Policy - TableChat"
        description="TableChat Privacy Policy. Learn how we collect, use, and protect restaurant and patron data in our NFC-powered communication system."
        canonical="https://tablechat.io/privacy"
      />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Privacy Policy', url: 'https://tablechat.io/privacy' },
      ])} />
      <div className="container">
        <div className="legal-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: March 18, 2026</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Scope</h2>
            <p>
              This Privacy Policy explains how TableChat collects, uses, discloses, and safeguards information in
              connection with our Services, including restaurant-facing applications, the patron web app, NFC-based
              table messaging, payment and billing flows, and related websites. It covers:
            </p>
            <ul>
              <li>Restaurant client and staff data used for accounts, administration, and billing.</li>
              <li>Patron interactions via the web app initiated from NFC tags (no patron registration required).</li>
            </ul>
          </section>

          <section>
            <h2>2. Roles and Responsibilities</h2>
            <p>
              <strong>Restaurant Client/Staff Data:</strong> We act as a controller for business contact, authentication,
              and billing information we collect to operate the Services.
            </p>
            <p>
              <strong>Patron Messages and Session Metadata:</strong> We generally act as a processor on behalf of the
              restaurant. Restaurants determine lawful bases and retention for patron content where applicable. A DPA is
              available upon request if required by law.
            </p>
            <p>
              As an interactive service provider, we facilitate communication between patrons and restaurants but do not
              create or control the content exchanged. Each user is responsible for their own statements and materials.
              Consistent with 47 U.S.C. § 230 and similar safe-harbor laws where applicable, we are not liable for
              user-generated content or messages transmitted through the platform.
            </p>
            <p>
              <strong>Visibility of Messages:</strong> Chat messages exchanged through the patron web app, connect mobile
              app, or manager console are visible to all participants in the associated table session (including restaurant
              staff and managers). While messages are transmitted and stored using secure methods, the chat is not a private
              or end-to-end encrypted communication channel. Users should not expect message content to remain confidential
              from other participants or the restaurant. Messages may be accessed or retained by the restaurant or
              TableChat as necessary for delivery, moderation, technical support, compliance, or auditing.
            </p>
          </section>

          <section>
            <h2>3. Information We Collect</h2>
            <h3>3.1 Information You Provide (Restaurant Clients/Staff)</h3>
            <ul>
              <li>Business contact details (e.g., name, email, phone), role/permissions.</li>
              <li>
                Account credentials (authentication via phone or Google sign-in), and staff profile details.
              </li>
              <li>Billing and payment details (handled by payment processors such as Stripe).</li>
              <li>Support communications and configuration data (e.g., restaurant/tables).</li>
            </ul>
            <h3>3.2 Information Provided by Patrons (No Registration)</h3>
            <ul>
              <li>Message content submitted through the patron web app (free-text or selections).</li>
              <li>Optional information a patron chooses to include in messages.</li>
            </ul>
            <p>Patrons do not create accounts; messages are associated to an NFC/table session.</p>
            <h3>3.5 Information Provided by Website Visitors</h3>
            <ul>
              <li>Contact form details (name, email, message) submitted through the TableChat website.</li>
              <li>
                Mobile phone number voluntarily provided for SMS marketing opt-in on the TableChat website,
                along with consent records (timestamp, policy version, IP address, and device information).
              </li>
            </ul>
            <p>
              Website visitors who opt in to SMS updates do not create accounts. Contact information is stored
              by TableChat for marketing purposes and is not associated with any restaurant.
            </p>
            <h3>3.3 Information Collected Automatically</h3>
            <ul>
              <li>Device and network metadata (e.g., IP address, browser or OS version).</li>
              <li>Service logs and error logs (e.g., component name, error type).</li>
              <li>Session identifiers tied to NFC/table context and routing.</li>
              <li>
                Limited client-side storage (e.g., sessionStorage for secure payment bridging when staff process
                payments).
              </li>
              <li>
                Cookies or similar technologies for essential site operations and security; we do not use third-party
                advertising cookies.
              </li>
            </ul>
            <h3>3.4 Payment Information</h3>
            <p>
              We use third-party payment processors (e.g., Stripe) to handle payment card data. We do not store full
              payment card numbers on our servers. Stripe processes payments in accordance with its terms and privacy
              policy.
            </p>
          </section>

          <section>
            <h2>4. Restaurants and Guest Data</h2>
            <p>
              Restaurants using the TableChat platform may collect guest contact information, such as phone numbers or
              email addresses, when guests interact with the platform during a visit (for example, by opting in to a
              loyalty or VIP program).
            </p>
            <p>
              The restaurant is the party collecting and controlling this information. TableChat processes guest contact
              information on behalf of the restaurant in order to provide the Services, including guest messaging,
              notifications, and promotional communications initiated by the restaurant.
            </p>
            <p>
              Contact information collected directly by TableChat through its website (tablechat.io) — such as phone
              numbers submitted for SMS product updates — is controlled by TableChat and is not shared with
              restaurants. TableChat acts as the controller for this data.
            </p>
          </section>

          <section>
            <h2>5. How We Use Information</h2>
            <ul>
              <li>
                Provide, operate, maintain, and improve the Services (including routing, messaging, session management,
                NFC assignment).
              </li>
              <li>Authenticate and authorize restaurant staff and enforce role-based access.</li>
              <li>Process subscriptions and one-time hardware charges; maintain billing records.</li>
              <li>Secure the Services (fraud and abuse prevention, anti-bot measures such as reCAPTCHA).</li>
              <li>Monitor service health, fix bugs, and improve quality (e.g., through error logs).</li>
              <li>Provide support and communicate about updates, material changes, and service notices.</li>
              <li>Comply with legal obligations and enforce our agreements.</li>
            </ul>
            <h3>SMS and Email Communications</h3>
            <p>
              Restaurants using the TableChat platform may invite guests to provide a phone number or email address in
              order to receive messages related to their visit, restaurant promotions, loyalty programs, or other
              restaurant communications. When a guest chooses to receive SMS or email messages, those communications are
              sent by the restaurant. TableChat may deliver those messages on behalf of the restaurant using messaging
              infrastructure providers.
            </p>
            <p>
              TableChat may also send SMS messages directly to individuals who opt in through the TableChat website
              for product updates, feature announcements, and promotional offers related to the TableChat platform.
              These messages are sent by TableChat as the sender of record and are not associated with any restaurant.
            </p>
            <p>
              Message frequency may vary. Standard message and data rates may apply depending on your mobile
              carrier. You may opt out of receiving messages at any time by replying STOP to a text message, clicking
              the unsubscribe link in an email, or by following other instructions provided in the message.
            </p>
          </section>

          <section>
            <h2>6. Disclosures and Sharing</h2>
            <p>We share information with:</p>
            <ul>
              <li>
                Service providers and subprocessors assisting in hosting, messaging, logging, security, analytics, and
                payments (e.g., Google/Firebase, Stripe).
              </li>
              <li>Restaurants receiving patron messages related to their tables/sessions.</li>
              <li>
                Professional advisors and authorities where legally required or to protect rights, safety, and security.
              </li>
            </ul>
            <p>
              These service providers process information only as necessary to perform services for TableChat and are
              not permitted to use the information for their own independent purposes.
            </p>
            <p>We do not sell personal information.</p>
          </section>

          <section>
            <h2>7. Cookies and Local Storage</h2>
            <p>
              We use essential cookies and similar technologies strictly for functionality and security. Where the
              payment flow is used by staff, the app may temporarily store minimal data in sessionStorage to complete
              payment steps. SessionStorage clears when the browser tab closes. We do not deploy third‑party
              advertising cookies.
            </p>
          </section>

          <section>
            <h2>8. Data Retention</h2>
            <p>
              We retain information for as long as necessary to provide the Services, comply with legal obligations,
              resolve disputes, and enforce agreements. Patron messages and session metadata are retained as needed for
              operations and auditing; where available, restaurants may configure retention. Payment and billing records
              are retained as required by law and our accounting policies. Message data may be stored in logs or backups
              for limited periods to ensure delivery, service reliability, or compliance, and such data is not considered
              private communications between individuals. Guest contact information collected through the platform may
              be retained by the restaurant for marketing and operational purposes; TableChat retains such data only as
              necessary to provide the Services, maintain system operations, and comply with legal obligations.
            </p>
          </section>

          <section>
            <h2>9. Security</h2>
            <p>
              We implement industry-standard technical and organizational measures to protect information, including
              platform safeguards (e.g., Firebase protections), application-level controls, and anti-abuse mechanisms. No
              method of transmission or storage is completely secure.
            </p>
          </section>

          <section>
            <h2>10. International Transfers</h2>
            <p>
              If information is transferred across borders, we implement appropriate safeguards permitted by applicable
              law.
            </p>
          </section>

          <section>
            <h2>11. Children's Privacy</h2>
            <p>
              The Services are intended for general restaurant use and are not directed to children under 13 (or the
              age required by local law). We do not knowingly collect personal information from children. If you
              believe a child has provided information, contact us to request deletion.
            </p>
          </section>

          <section>
            <h2>12. Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have rights to access, correct, delete, or restrict processing of
              your personal information, or request portability. Restaurant patrons seeking to exercise rights related
              to patron messages should generally contact the restaurant; we can support requests in our capacity as
              processor. For staff or direct account data, contact us at{' '}
              <a href="mailto:support@tablechat.io">support@tablechat.io</a>.
            </p>
          </section>

          <section>
            <h2>13. Third-Party Services</h2>
            <p>
              Your use of third-party services integrated with the Services (e.g., Stripe payments, Google/Firebase
              infrastructure, reCAPTCHA anti-abuse) is subject to their terms and privacy policies. We encourage you to
              review those policies.
            </p>
          </section>

          <section>
            <h2>14. Promotional and Marketing Messages</h2>
            <h3>Restaurant Opt-In</h3>
            <p>
              If you voluntarily opt in to a restaurant's loyalty or VIP program through the TableChat platform, your
              contact information (email address and/or phone number) may be collected and used to send you promotional
              and marketing messages. These messages may be sent by TableChat on behalf of the restaurant, by the
              restaurant directly, or through the restaurant's chosen third-party marketing platform (e.g., Klaviyo,
              Mailchimp, Constant Contact).
            </p>
            <h3>Website Opt-In</h3>
            <p>
              If you voluntarily provide your phone number through the TableChat website and consent to receive
              messages, TableChat may send you SMS messages about product updates, feature announcements, and
              promotional offers. TableChat is the sender of record for these messages. Your contact information
              is not shared with restaurants or their marketing service providers.
            </p>
            <p>
              For full details about promotional messaging — including what information is collected, who may contact
              you, message frequency, how to opt out, and your consent rights — please see our{' '}
              <a href="/messaging-policy">Promotional Messaging Policy</a>.
            </p>
          </section>

          <section>
            <h2>15. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The updated version will be posted with a new "Last
              Updated" date and, where required, notified through the Services.
            </p>
          </section>

          <section>
            <h2>16. Contact</h2>
            <p>
              For questions or requests regarding this Policy or data protection, contact us at{' '}
              <a href="mailto:support@tablechat.io">support@tablechat.io</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
